import { createClient } from "@supabase/supabase-js";
import { fetchEvidencePhoto } from "./photoPreview";
import type {
  Dashboard,
  Evidence,
  SearchFilters,
  SearchResult,
  VisitInput,
  SavedSearch,
  Buddy,
  Product,
  Store,
} from "@little-finds/core";
import { searchEvidence, products } from "@little-finds/core";
import {
  demoStores,
  readDemo,
  demoSubmit,
  demoModerate,
  demoFeed,
  demoSave,
} from "./demo";
const url = import.meta.env.VITE_SUPABASE_URL,
  key = import.meta.env.VITE_SUPABASE_ANON_KEY;
export const configured = Boolean(url && key);
export const supabase = configured ? createClient(url, key) : null;
export const demo = new URLSearchParams(location.search).get("demo") === "1";
export class RpcError extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = "RpcError";
  }
}
export async function rpc<T>(
  name: string,
  args: Record<string, unknown> = {},
): Promise<T> {
  if (!supabase)
    throw new Error(
      "The shared service is not connected yet. You can explore the demo while it is being set up.",
    );
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw new RpcError(error.message, error.code);
  return data as T;
}
export const api = {
  stores: (query = "") =>
    demo
      ? Promise.resolve(
          demoStores.filter((s) =>
            `${s.name} ${s.city} ${s.zip}`
              .toLowerCase()
              .includes(query.toLowerCase()),
          ),
        )
      : rpc<Store[]>("find_stores", { p_query: query }),
  store: (id: string) =>
    demo
      ? Promise.resolve(demoStores.find((s) => s.id === id) ?? null)
      : rpc<Store | null>("get_store", { p_store_id: id }),
  storePage: (query = "", offset = 0) =>
    demo
      ? Promise.resolve({
          stores: demoStores
            .filter((s) =>
              `${s.name} ${s.city} ${s.zip}`
                .toLowerCase()
                .includes(query.toLowerCase()),
            )
            .slice(offset, offset + 50),
          has_more: false,
        })
      : rpc<{ stores: Store[]; has_more: boolean }>("find_store_page", {
          p_query: query,
          p_offset: offset,
          p_limit: 50,
        }),
  search: (f: SearchFilters) =>
    demo
      ? Promise.resolve(searchEvidence(demoStores, readDemo().evidence, f))
      : configured
        ? rpc<SearchResult>("search_locations", { p_filters: f })
        : Promise.resolve({
            stores: [],
            clusters: [],
            total_matches: 0,
            recent_stores: 0,
            generated_at: new Date().toISOString(),
            has_more: false,
          }),
  catalog: () =>
    configured && !demo
      ? rpc<Product[]>("get_catalog")
      : Promise.resolve(products),
  evidence: (id: string) =>
    demo
      ? Promise.resolve(
          readDemo().evidence.filter(
            (e) => e.store_id === id && e.moderation === "approved",
          ),
        )
      : rpc<Evidence[]>("get_store_evidence", { p_store_id: id }),
  dashboard: () =>
    demo
      ? Promise.resolve(readDemo().dashboard)
      : rpc<Dashboard>("get_my_dashboard"),
  submit: (v: VisitInput) =>
    demo
      ? Promise.resolve(demoSubmit(v))
      : rpc<{ id: string; status: string }>("submit_visit", { p_visit: v }),
  save: (s: SavedSearch) =>
    demo
      ? Promise.resolve(demoSave(s))
      : rpc<SavedSearch>("save_search", { p_search: s }),
  feed: (id: string) =>
    demo
      ? Promise.resolve(demoFeed(id))
      : rpc<Buddy>("feed_buddy", { p_request_id: id }),
  moderate: async (id: string, decision: string, note = "") =>
    demo
      ? demoModerate(id, decision)
      : rpc("moderate_visit", {
          p_visit_id: id,
          p_decision: decision,
          p_note: note,
        }),
  admin: () =>
    demo
      ? Promise.resolve({
          visits: readDemo().visits,
          suggestions: [],
          reports: [],
          sources: [],
          users: [],
          metrics: { mode: "demonstration" },
        })
      : rpc<Record<string, any>>("get_admin_queue"),
};
export async function preparePhoto(file: File): Promise<Blob> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type))
    throw new Error("Choose a JPEG, PNG, or WebP photo.");
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Choose a photo smaller than 10 MB.");
  const bitmap = await createImageBitmap(file),
    canvas = document.createElement("canvas"),
    scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const context = canvas.getContext("2d");
  if (!context) {
    bitmap.close();
    throw new Error("This browser could not prepare the photo.");
  }
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (v) =>
        v ? resolve(v) : reject(new Error("Could not prepare this photo.")),
      "image/jpeg",
      0.85,
    ),
  );
  return blob;
}
export async function uploadPhoto(file: File, requestId: string) {
  const blob = await preparePhoto(file);
  if (demo) return undefined;
  if (!supabase) throw new Error("Sign in before uploading a shelf photo.");
  const form = new FormData();
  form.set("photo", blob, "sighting.jpg");
  form.set("request_id", requestId);
  const { data, error } = await supabase!.functions.invoke("upload-photo", {
    body: form,
  });
  if (error) throw error;
  if (!data || typeof data.photo_path !== "string")
    throw new Error("The photo could not be verified. Please retry.");
  return data.photo_path as string;
}

export async function loadEvidencePhoto(
  evidenceId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  if (!supabase || !url || !key || demo)
    throw new Error("A live service is needed to view this photo.");
  const { data: before, error } = await supabase.auth.getSession();
  if (error) throw error;
  const photo = await fetchEvidencePhoto({
    serviceURL: url,
    publicKey: key,
    accessToken: before.session?.access_token,
    evidenceId,
    signal,
  });
  const { data: after } = await supabase.auth.getSession();
  if (before.session?.user.id !== after.session?.user.id)
    throw new Error("Your account changed. Open the photo again.");
  return photo;
}
